import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { signOut } from "../auth/AuthGate";
import { apiDelete, apiGet, apiPatch, apiPost } from "../api";
import posthog from "../posthog";
import { ConfirmDialog } from "./ConfirmDialog";
function StatusBadge({ status }) {
    const cls = {
        unused: "bg-emerald-100 text-emerald-700",
        used: "bg-slate-100 text-slate-500",
        expired: "bg-red-100 text-red-600",
    }[status];
    const label = { unused: "Offen", used: "Verwendet", expired: "Abgelaufen" }[status];
    return (_jsx("span", { className: `inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`, children: label }));
}
function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);
    function copy() {
        void navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }
    return (_jsx("button", { onClick: copy, className: "px-2 py-1 text-xs border border-slate-200 rounded cursor-pointer bg-transparent hover:bg-slate-50 transition-colors", children: copied ? "Kopiert!" : "Link kopieren" }));
}
export default function GroupAdminDashboard() {
    const [displayName, setDisplayName] = useState("");
    const [editingName, setEditingName] = useState(false);
    const [nameInput, setNameInput] = useState("");
    const [nameError, setNameError] = useState("");
    const [nameSaving, setNameSaving] = useState(false);
    const [members, setMembers] = useState([]);
    const [membersError, setMembersError] = useState("");
    const [codes, setCodes] = useState([]);
    const [codeCount, setCodeCount] = useState(1);
    const [generateError, setGenerateError] = useState("");
    const [generating, setGenerating] = useState(false);
    const [codesError, setCodesError] = useState("");
    const loadMembers = useCallback(async () => {
        const data = await apiGet("/api/groups/members");
        setMembers(data.members);
    }, []);
    const loadCodes = useCallback(async () => {
        const data = await apiGet("/api/groups/invite-codes");
        setCodes(data.codes);
    }, []);
    useEffect(() => {
        void apiGet("/api/groups/my-group").then((data) => {
            setDisplayName(data.displayName);
            setNameInput(data.displayName);
        });
        void loadMembers();
        void loadCodes();
    }, [loadMembers, loadCodes]);
    async function saveName() {
        setNameError("");
        if (!nameInput.trim()) {
            setNameError("Name erforderlich");
            return;
        }
        setNameSaving(true);
        try {
            await apiPatch("/api/groups/display-name", {
                displayName: nameInput.trim(),
            });
            posthog.capture("group_name_updated");
            setDisplayName(nameInput.trim());
            setEditingName(false);
        }
        catch (e) {
            setNameError(e instanceof Error ? e.message : "Fehler");
        }
        finally {
            setNameSaving(false);
        }
    }
    async function updateMemberRole(memberId, role) {
        setMembersError("");
        try {
            await apiPatch(`/api/groups/members/${memberId}/role`, { role });
            posthog.capture("group_member_role_updated", { new_role: role });
            await loadMembers();
        }
        catch (e) {
            setMembersError(e instanceof Error ? e.message : "Fehler");
        }
    }
    const [confirmRemoveMember, setConfirmRemoveMember] = useState(null);
    async function removeMember(memberId) {
        setConfirmRemoveMember(null);
        setMembersError("");
        try {
            await apiDelete(`/api/groups/members/${memberId}`);
            posthog.capture("group_member_removed");
            await loadMembers();
        }
        catch (e) {
            setMembersError(e instanceof Error ? e.message : "Fehler");
        }
    }
    async function generateCodes() {
        setGenerateError("");
        if (isNaN(codeCount) || codeCount < 1) {
            setGenerateError("Ungültige Anzahl");
            return;
        }
        setGenerating(true);
        try {
            await apiPost("/api/groups/invite-codes", { count: codeCount });
            posthog.capture("group_invite_codes_generated", {
                requested_count: codeCount,
            });
            await loadCodes();
        }
        catch (e) {
            setGenerateError(e instanceof Error ? e.message : "Fehler");
        }
        finally {
            setGenerating(false);
        }
    }
    async function revokeCode(codeId) {
        setCodesError("");
        try {
            await apiDelete(`/api/groups/invite-codes/${codeId}`);
            posthog.capture("group_invite_code_revoked");
            await loadCodes();
        }
        catch (e) {
            setCodesError(e instanceof Error ? e.message : "Fehler");
        }
    }
    const origin = window.location.origin;
    function escapeHtml(s) {
        return s.replace(/[&<>"']/g, (c) => ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#39;",
        })[c]);
    }
    function printActiveCodes() {
        const active = codes.filter((c) => c.status === "unused");
        const rows = active
            .map((c) => `
      <tr>
        <td>${escapeHtml(c.code)}</td>
        <td>${escapeHtml(`${origin}/join?code=${c.code}`)}</td>
        <td>${new Date(c.expiresAt).toLocaleDateString("de-DE")}</td>
      </tr>
    `)
            .join("");
        const html = `<!doctype html>
<html lang="de">
<head>
<meta charset="utf-8" />
<title>Aktive Einladungscodes – ${escapeHtml(displayName)}</title>
<style>
  body { font-family: sans-serif; padding: 24px; color: #0f172a; }
  h1 { font-size: 18px; margin-bottom: 4px; }
  p.meta { color: #64748b; font-size: 12px; margin-top: 0; margin-bottom: 20px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th, td { text-align: left; padding: 8px 10px; border-bottom: 1px solid #e2e8f0; }
  th { text-transform: uppercase; font-size: 11px; color: #64748b; letter-spacing: 0.03em; }
  td:first-child { font-family: monospace; font-weight: 600; letter-spacing: 0.05em; }
</style>
</head>
<body>
  <h1>Aktive Einladungscodes – ${escapeHtml(displayName)}</h1>
  <p class="meta">Stand: ${new Date().toLocaleDateString("de-DE")}</p>
  <table>
    <thead><tr><th>Code</th><th>Link</th><th>Läuft ab</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
</body>
</html>`;
        const win = window.open("", "_blank");
        if (!win)
            return;
        win.document.open();
        win.document.write(html);
        win.document.close();
        win.focus();
        win.print();
    }
    const roleBadgeCls = (role) => role === "owner"
        ? "bg-purple-100 text-purple-700"
        : role === "admin"
            ? "bg-blue-100 text-blue-700"
            : "bg-slate-100 text-slate-500";
    const roleLabel = (role) => role === "owner" ? "Eigentümer" : role === "admin" ? "Admin" : "Mitglied";
    return (_jsxs("div", { className: "min-h-screen bg-slate-50 text-slate-900 font-sans", children: [_jsxs("header", { className: "bg-white border-b border-slate-200 h-14 flex items-center justify-between px-6 gap-4", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("span", { className: "text-base font-bold", children: displayName }), _jsx("button", { onClick: () => setEditingName(true), title: "Namen bearbeiten", className: "text-xs text-slate-400 hover:text-slate-600 border border-slate-200 rounded px-2 py-0.5 cursor-pointer bg-transparent", children: "\u270F\uFE0F" })] }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("button", { onClick: () => window.location.replace("/play"), className: "px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg cursor-pointer border-0 transition-colors", children: "Spielen" }), _jsx("button", { onClick: () => void signOut()
                                    .catch(() => { })
                                    .finally(() => {
                                    window.location.replace("/");
                                }), className: "px-3 py-1.5 text-sm text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer bg-transparent", children: "Abmelden" })] })] }), editingName && (_jsxs("div", { className: "bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-3", children: [_jsx("input", { value: nameInput, onChange: (e) => setNameInput(e.target.value), type: "text", className: "w-64 px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" }), _jsx("button", { onClick: () => void saveName(), disabled: nameSaving, className: "px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg cursor-pointer border-0 transition-colors disabled:opacity-50", children: "Speichern" }), _jsx("button", { onClick: () => {
                            setEditingName(false);
                            setNameInput(displayName);
                            setNameError("");
                        }, className: "px-4 py-1.5 text-slate-500 border border-slate-200 rounded-lg text-sm cursor-pointer bg-transparent hover:bg-slate-50", children: "Abbrechen" }), nameError && (_jsx("span", { className: "text-red-500 text-xs", children: nameError }))] })), _jsxs("main", { className: "max-w-3xl mx-auto px-6 py-8 space-y-6", children: [_jsxs("div", { className: "bg-white border border-slate-200 rounded-xl p-6", children: [_jsx("h2", { className: "text-base font-semibold mb-4", children: "Mitglieder" }), members.length === 0 ? (_jsx("p", { className: "text-slate-400 text-sm", children: "Noch keine Mitglieder." })) : (_jsx("div", { className: "overflow-x-auto", children: _jsxs("table", { className: "w-full text-sm border-collapse", children: [_jsx("thead", { children: _jsxs("tr", { className: "border-b-2 border-slate-200", children: [_jsx("th", { className: "text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide", children: "Name" }), _jsx("th", { className: "text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide", children: "Code" }), _jsx("th", { className: "text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide", children: "E-Mail" }), _jsx("th", { className: "text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide", children: "Rolle" }), _jsx("th", { className: "text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide", children: "Beigetreten" }), _jsx("th", { className: "px-3 py-2" })] }) }), _jsx("tbody", { children: members.map((m) => (_jsxs("tr", { className: "border-b border-slate-100 last:border-0 hover:bg-slate-50", children: [_jsx("td", { className: "px-3 py-2 font-medium", children: m.name }), _jsx("td", { className: "px-3 py-2 font-mono text-xs text-slate-600", children: m.playerCode ?? "—" }), _jsx("td", { className: "px-3 py-2 text-slate-500", children: m.email }), _jsx("td", { className: "px-3 py-2", children: _jsx("span", { className: `inline-block px-2 py-0.5 rounded text-xs font-medium ${roleBadgeCls(m.role)}`, children: roleLabel(m.role) }) }), _jsx("td", { className: "px-3 py-2 text-slate-500", children: new Date(m.joinedAt).toLocaleDateString("de-DE") }), _jsx("td", { className: "px-3 py-2", children: m.role !== "owner" && (_jsxs("div", { className: "flex gap-2", children: [m.role === "member" ? (_jsx("button", { onClick: () => void updateMemberRole(m.id, "admin"), className: "px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded cursor-pointer border-0 transition-colors", children: "Zum Admin" })) : (_jsx("button", { onClick: () => void updateMemberRole(m.id, "member"), className: "px-2 py-1 text-xs border border-slate-200 rounded cursor-pointer bg-transparent hover:bg-slate-50 text-slate-600 transition-colors", children: "Zu Mitglied" })), _jsx("button", { onClick: () => setConfirmRemoveMember({
                                                                        id: m.id,
                                                                        name: m.name,
                                                                    }), className: "px-2 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded cursor-pointer border-0 transition-colors", children: "Entfernen" })] })) })] }, m.id))) })] }) })), membersError && (_jsx("p", { className: "text-red-500 text-xs mt-2", children: membersError }))] }), _jsxs("div", { className: "bg-white border border-slate-200 rounded-xl p-6", children: [_jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsx("h2", { className: "text-base font-semibold", children: "Einladungscodes" }), _jsx("button", { onClick: printActiveCodes, disabled: codes.every((c) => c.status !== "unused"), className: "px-3 py-1.5 text-xs border border-slate-200 rounded-lg cursor-pointer bg-transparent hover:bg-slate-50 text-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed", children: "Aktive Codes drucken" })] }), _jsxs("div", { className: "flex gap-3 items-center mb-5", children: [_jsx("input", { type: "number", min: 1, max: 50, value: codeCount, onChange: (e) => setCodeCount(parseInt(e.target.value, 10)), className: "w-20 px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" }), _jsx("button", { onClick: () => void generateCodes(), disabled: generating, className: "px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg cursor-pointer border-0 transition-colors disabled:opacity-50", children: "Codes erstellen" }), generateError && (_jsx("span", { className: "text-red-500 text-xs", children: generateError }))] }), codesError && (_jsx("p", { className: "text-red-500 text-xs mb-3", children: codesError })), codes.length === 0 ? (_jsx("p", { className: "text-slate-400 text-sm", children: "Noch keine Codes erstellt." })) : (_jsx("div", { className: "overflow-x-auto", children: _jsxs("table", { className: "w-full text-sm border-collapse", children: [_jsx("thead", { children: _jsxs("tr", { className: "border-b-2 border-slate-200", children: [_jsx("th", { className: "text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide", children: "Code" }), _jsx("th", { className: "text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide", children: "Erstellt" }), _jsx("th", { className: "text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide", children: "L\u00E4uft ab" }), _jsx("th", { className: "text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide", children: "Status" }), _jsx("th", { className: "text-left px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide", children: "Link" }), _jsx("th", { className: "px-3 py-2" })] }) }), _jsx("tbody", { children: codes.map((c) => (_jsxs("tr", { className: "border-b border-slate-100 last:border-0 hover:bg-slate-50", children: [_jsx("td", { className: "px-3 py-2 font-mono font-semibold tracking-widest text-base", children: c.code }), _jsx("td", { className: "px-3 py-2 text-slate-500", children: new Date(c.createdAt).toLocaleDateString("de-DE") }), _jsx("td", { className: "px-3 py-2 text-slate-500", children: new Date(c.expiresAt).toLocaleDateString("de-DE") }), _jsx("td", { className: "px-3 py-2", children: _jsx(StatusBadge, { status: c.status }) }), _jsxs("td", { className: "px-3 py-2", children: [c.status === "unused" && (_jsx(CopyButton, { text: `${origin}/join?code=${c.code}` })), c.status !== "unused" && "—"] }), _jsx("td", { className: "px-3 py-2", children: c.status === "unused" && (_jsx("button", { onClick: () => void revokeCode(c.id), className: "px-2 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded cursor-pointer border-0 transition-colors", children: "Sperren" })) })] }, c.id))) })] }) }))] })] }), confirmRemoveMember && (_jsx(ConfirmDialog, { title: "Mitglied entfernen", message: `${confirmRemoveMember.name} aus der Gruppe entfernen?`, confirmLabel: "Entfernen", onConfirm: () => void removeMember(confirmRemoveMember.id), onCancel: () => setConfirmRemoveMember(null) }))] }));
}
